import { PLOT_MATERIALS } from '@dg-embalajes/product-domain';
import { Eye, EyeOff, LocateFixed, Maximize, RotateCcw, RotateCw, Settings2 } from 'lucide-react';
import type { EditorPort, EditorSnapshot } from '@/ports/editor';
import { SegmentedControl } from '../SegmentedControl';

export function TablePanel({ editor, snapshot, onReconfigure }: { readonly editor: EditorPort; readonly snapshot: EditorSnapshot; readonly onReconfigure: () => void }) {
  const product = snapshot.document?.product;
  if (!product) return null;
  const width = product.kind === 'tube' ? product.developmentEffectiveMm : product.widthMm;
  const height = product.kind === 'tube' ? product.heightMm : product.heightMm;
  const orientationOptions: readonly { readonly value: '0' | '90'; readonly label: string }[] = product.kind === 'tube'
    ? [{ value: '0', label: '0° · vista horizontal' }, { value: '90', label: '90° · ver tubo acostado' }]
    : [{ value: '0', label: '0° · vista horizontal' }, { value: '90', label: '90° · vista vertical' }];

  return (
    <div className="panel-stack" data-testid="table-panel">
      <div className="measure-strip">
        <span><strong>{product.kind === 'tube' ? 'DESARROLLO' : 'ANCHO'}</strong>{width} mm</span>
        <span><strong>ALTO</strong>{height} mm</span>
      </div>

      <div className="control-grid control-grid--three">
        <button type="button" onClick={() => editor.fitCamera()}><Maximize /> Ver completo</button>
        <button type="button" onClick={() => editor.centerCamera()}><LocateFixed /> Centrar</button>
        <button type="button" onClick={() => editor.resetCamera()}><RotateCcw /> Restablecer vista</button>
      </div>

      <div className="field-control">
        <div className="field-control__label"><label htmlFor="camera-zoom">Zoom de mesa</label><output>{snapshot.camera.zoomPercent}%</output></div>
        <input
          id="camera-zoom"
          type="range"
          min="50"
          max="500"
          step="1"
          value={snapshot.camera.zoomPercent}
          onChange={(event) => editor.setCameraZoom(Number(event.target.value))}
        />
        <small>Entre 50% y 500%. Ver completo permanece siempre disponible.</small>
      </div>

      <div className="field-control">
        <span className="field-control__title">Vista de la mesa</span>
        <SegmentedControl
          compact
          label="Orientación de la mesa"
          value={String(snapshot.camera.orientationDeg) as '0' | '90'}
          options={orientationOptions}
          onChange={(value) => editor.setCameraOrientation(Number(value) as 0 | 90)}
        />
        <div className="control-grid control-grid--two">
          <button type="button" onClick={() => editor.rotateCameraBy(-15)}><RotateCcw /> Rotar vista -15°</button>
          <button type="button" onClick={() => editor.rotateCameraBy(15)}><RotateCw /> Rotar vista +15°</button>
        </div>
        <small>La rotación es sólo visual. No cambia medidas, objetos ni PDF.</small>
      </div>

      <button className="toggle-row" type="button" aria-pressed={snapshot.camera.showDimensions} onClick={() => editor.setDimensionsVisible(!snapshot.camera.showDimensions)}>
        {snapshot.camera.showDimensions ? <Eye /> : <EyeOff />}
        <span><strong>Cotas en pantalla</strong><small>ALTO, DESARROLLO y Archivo real nunca se imprimen.</small></span>
      </button>

      {product.kind === 'tube' ? (
        <div className="field-control">
          <label htmlFor="material-editor">Material del ploteo</label>
          <select id="material-editor" value={product.materialId} onChange={(event) => editor.changeTubeMaterial(event.target.value as typeof product.materialId)}>
            {PLOT_MATERIALS.map((material) => <option key={material.id} value={material.id}>{material.label}</option>)}
          </select>
          <dl className="compact-summary">
            <div><dt>Desarrollo del archivo</dt><dd>{product.developmentEffectiveMm} mm</dd></div>
          </dl>
        </div>
      ) : null}

      <div className="bleed-note">
        <strong>{product.kind === 'tube' ? 'Sangre y recorte físico' : 'Zona segura y línea de corte'}</strong>
        {product.kind === 'tube' ? (
          <p>Los objetos pueden cruzar el borde. El PDF recorta al documento y mantiene al menos 4 mm del objeto dentro de la mesa para poder seleccionarlo.</p>
        ) : (
          <p>La zona segura usa 7% de margen en rectángulo/cuadrado y radio 42% en círculo. Sólo aparece en el editor. La línea de corte es discontinua acá y continua en el PDF.</p>
        )}
      </div>

      <button className="secondary-action" type="button" onClick={onReconfigure}><Settings2 /> Cambiar medida</button>
    </div>
  );
}
